'use client'

import { type ReactNode, useEffect, useRef, useState } from 'react'

interface MobileTocClientProps {
  sectionIds: string[]
  children: ReactNode
}

export const MobileTocClient = ({ sectionIds, children }: MobileTocClientProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  // Show after scrolling past the TOC's natural position
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    // Record the TOC's position in the document on mount
    const threshold = el.offsetTop

    const onScroll = () => {
      setVisible(window.scrollY >= threshold)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Track active section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const links = containerRef.current?.querySelectorAll('[data-toc-link]')
            links?.forEach((link) => {
              if (link.getAttribute('data-toc-link') === entry.target.id) {
                link.setAttribute('data-active', '')
              } else {
                link.removeAttribute('data-active')
              }
            })
          }
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    )

    for (const id of sectionIds) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [sectionIds])

  return (
    <div ref={containerRef} className="sticky top-0 z-30 h-0 lg:hidden">
      <div
        className="transition-[transform,opacity] duration-200"
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(-100%)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  )
}
